import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import Modal from '../../components/common/Modal';

const ProductManagement = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    sku: '',
    sapCode: '',
    productType: '',
    unit: 'Unité',
    unitPrice: 0,
    tva: 19,
  });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    // Only directrice_marketing should access this page
    if (user && user.role !== 'directrice_marketing') {
      window.location.href = '/dashboard';
    } else {
      fetchProducts();
      fetchCategories();
    }
  }, [user]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/products');
      setProducts(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Erreur lors du chargement des produits');
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/api/products/categories');
      setCategories(response.data.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Erreur lors du chargement des catégories');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Le prix et la TVA doivent partir en nombres : la TVA est contrainte par
    // une enum [0, 19] côté modèle, et le prix alimente le calcul de l'avoir.
    let valeur = value;
    if (type === 'checkbox') valeur = checked;
    else if (name === 'unitPrice') valeur = value === '' ? '' : Number(value);
    else if (name === 'tva') valeur = Number(value);

    setFormData({
      ...formData,
      [name]: valeur,
    });
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    
    try {
      setFormLoading(true);
      
      await api.post('/api/products', formData);
      
      toast.success('Produit ajouté avec succès');
      setShowAddModal(false);
      setFormData({
        name: '',
        description: '',
        category: '',
        sku: '',
        sapCode: '',
        productType: '',
        unit: 'Unité',
        unitPrice: 0,
        tva: 19,
      });
      fetchProducts();
      setFormLoading(false);
    } catch (error) {
      console.error('Error adding product:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de l\'ajout du produit');
      setFormLoading(false);
    }
  };

  const handleEditProduct = async (e) => {
    e.preventDefault();
    
    if (!selectedProduct) return;
    
    try {
      setFormLoading(true);
      
      await api.put(`/api/products/${selectedProduct._id}`, formData);
      
      toast.success('Produit mis à jour avec succès');
      setShowEditModal(false);
      fetchProducts();
      setFormLoading(false);
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la mise à jour du produit');
      setFormLoading(false);
    }
  };

  const handleDeleteProduct = async () => {
    if (!selectedProduct) return;
    
    try {
      setFormLoading(true);
      
      await api.delete(`/api/products/${selectedProduct._id}`);
      
      toast.success('Produit supprimé avec succès');
      setShowDeleteModal(false);
      fetchProducts();
      setFormLoading(false);
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la suppression du produit');
      setFormLoading(false);
    }
  };

  const openEditModal = (product) => {
    setSelectedProduct(product);
    setFormData({
      name: product.name,
      description: product.description || '',
      category: product.category,
      sku: product.sku || '',
      sapCode: product.sapCode || '',
      productType: product.productType || '',
      unit: product.unit || 'Unité',
      unitPrice: product.unitPrice ?? 0,
      tva: product.tva ?? 19,
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (product) => {
    setSelectedProduct(product);
    setShowDeleteModal(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Gestion des produits</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Ajouter un produit
        </button>
      </div>
      
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Catégorie</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prix unitaire</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code SAP</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {products.length > 0 ? (
                products.map((product) => (
                  <tr key={product._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{product.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.category}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {product.unitPrice ? (
                        <>
                          <span className="text-gray-900 font-medium">
                            {product.unitPrice.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DA
                          </span>
                          <span className="text-xs text-gray-400 ml-1">
                            / {product.unit || 'Unité'} · TVA {product.tva ?? 19} %
                          </span>
                        </>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                          Prix non renseigné
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">{product.sku || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">{product.sapCode || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {product.productType === 'frais' ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Frais
                        </span>
                      ) : product.productType === 'uht' ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          UHT
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          Non défini
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {product.isActive === false ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Inactif
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Actif
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => openEditModal(product)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        Modifier
                      </button>
                      <button
                        onClick={() => openDeleteModal(product)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center text-sm text-gray-500">
                    Aucun produit trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Add Product Modal */}
      {showAddModal && (
        <Modal
          open
          onClose={() => setShowAddModal(false)}
          title="Ajouter un produit"
          size="md"
          footer={
            <>
              <button
                type="button"
                className="modal-btn-secondary"
                onClick={() => setShowAddModal(false)}
                disabled={formLoading}
              >
                Annuler
              </button>
              <button
                type="submit"
                form="product-add-form"
                className="modal-btn-primary"
                disabled={formLoading}
              >
                {formLoading ? 'Ajout...' : 'Ajouter'}
              </button>
            </>
          }
        >
          <form id="product-add-form" onSubmit={handleAddProduct}>
            <div className="space-y-4">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                            Nom
                          </label>
                          <input
                            type="text"
                            name="name"
                            id="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                            Description
                          </label>
                          <textarea
                            name="description"
                            id="description"
                            rows="3"
                            value={formData.description}
                            onChange={handleChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          ></textarea>
                        </div>
                        <div>
                          <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                            Catégorie
                          </label>
                          <select
                            name="category"
                            id="category"
                            value={formData.category}
                            onChange={handleChange}
                            required
                            className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          >
                            <option value="">Sélectionner une catégorie</option>
                            {categories.map((category) => (
                              <option key={category} value={category}>
                                {category}
                              </option>
                            ))}
                            <option value="Autre">Autre</option>
                          </select>
                        </div>
                        <div>
                          <label htmlFor="productType" className="block text-sm font-medium text-gray-700">
                            Type de produit <span className="text-red-500">*</span>
                          </label>
                          <select
                            name="productType"
                            id="productType"
                            value={formData.productType}
                            onChange={handleChange}
                            required
                            className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          >
                            <option value="">Sélectionner un type</option>
                            <option value="frais">Frais</option>
                            <option value="uht">UHT</option>
                          </select>
                        </div>
                        <div>
                          <label htmlFor="sku" className="block text-sm font-medium text-gray-700">
                            SKU
                          </label>
                          <input
                            type="text"
                            name="sku"
                            id="sku"
                            value={formData.sku}
                            onChange={handleChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="sapCode" className="block text-sm font-medium text-gray-700">
                            Code SAP
                          </label>
                          <input
                            type="text"
                            name="sapCode"
                            id="sapCode"
                            value={formData.sapCode}
                            onChange={handleChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            placeholder="Ex: 12345678"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label htmlFor="unitPrice" className="block text-sm font-medium text-gray-700">
                              Prix unitaire (DA)
                            </label>
                            <input
                              type="number"
                              name="unitPrice"
                              id="unitPrice"
                              min="0"
                              step="0.01"
                              value={formData.unitPrice}
                              onChange={handleChange}
                              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            />
                          </div>
                          <div>
                            <label htmlFor="tva" className="block text-sm font-medium text-gray-700">
                              TVA
                            </label>
                            <select
                              name="tva"
                              id="tva"
                              value={formData.tva}
                              onChange={handleChange}
                              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            >
                              <option value={19}>19 %</option>
                              <option value={0}>0 %</option>
                            </select>
                          </div>
                          <div>
                            <label htmlFor="unit" className="block text-sm font-medium text-gray-700">
                              Unité
                            </label>
                            <input
                              type="text"
                              name="unit"
                              id="unit"
                              value={formData.unit}
                              onChange={handleChange}
                              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                              placeholder="Brique, Pot…"
                            />
                          </div>
                        </div>
              <p className="text-xs text-gray-500 -mt-2">
                Le prix unitaire et la TVA servent au calcul des montants de l'avoir.
              </p>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit Product Modal */}
      {showEditModal && selectedProduct && (
        <Modal
          open
          onClose={() => setShowEditModal(false)}
          title="Modifier le produit"
          size="md"
          footer={
            <>
              <button
                type="button"
                className="modal-btn-secondary"
                onClick={() => setShowEditModal(false)}
                disabled={formLoading}
              >
                Annuler
              </button>
              <button
                type="submit"
                form="product-edit-form"
                className="modal-btn-primary"
                disabled={formLoading}
              >
                {formLoading ? 'Mise à jour...' : 'Mettre à jour'}
              </button>
            </>
          }
        >
          <form id="product-edit-form" onSubmit={handleEditProduct}>
            <div className="space-y-4">
                        <div>
                          <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700">
                            Nom
                          </label>
                          <input
                            type="text"
                            name="name"
                            id="edit-name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700">
                            Description
                          </label>
                          <textarea
                            name="description"
                            id="edit-description"
                            rows="3"
                            value={formData.description}
                            onChange={handleChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          ></textarea>
                        </div>
                        <div>
                          <label htmlFor="edit-category" className="block text-sm font-medium text-gray-700">
                            Catégorie
                          </label>
                          <select
                            name="category"
                            id="edit-category"
                            value={formData.category}
                            onChange={handleChange}
                            required
                            className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          >
                            <option value="">Sélectionner une catégorie</option>
                            {categories.map((category) => (
                              <option key={category} value={category}>
                                {category}
                              </option>
                            ))}
                            <option value="Autre">Autre</option>
                          </select>
                        </div>
                        <div>
                          <label htmlFor="editProductType" className="block text-sm font-medium text-gray-700">
                            Type de produit
                          </label>
                          <select
                            name="productType"
                            id="editProductType"
                            value={formData.productType}
                            onChange={handleChange}
                            className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          >
                            <option value="">Sélectionner un type</option>
                            <option value="frais">Frais</option>
                            <option value="uht">UHT</option>
                          </select>
                        </div>
                        <div>
                          <label htmlFor="edit-sku" className="block text-sm font-medium text-gray-700">
                            SKU
                          </label>
                          <input
                            type="text"
                            name="sku"
                            id="edit-sku"
                            value={formData.sku}
                            onChange={handleChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="edit-sapCode" className="block text-sm font-medium text-gray-700">
                            Code SAP
                          </label>
                          <input
                            type="text"
                            name="sapCode"
                            id="edit-sapCode"
                            value={formData.sapCode}
                            onChange={handleChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            placeholder="Ex: 12345678"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label htmlFor="edit-unitPrice" className="block text-sm font-medium text-gray-700">
                              Prix unitaire (DA)
                            </label>
                            <input
                              type="number"
                              name="unitPrice"
                              id="edit-unitPrice"
                              min="0"
                              step="0.01"
                              value={formData.unitPrice}
                              onChange={handleChange}
                              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            />
                          </div>
                          <div>
                            <label htmlFor="edit-tva" className="block text-sm font-medium text-gray-700">
                              TVA
                            </label>
                            <select
                              name="tva"
                              id="edit-tva"
                              value={formData.tva}
                              onChange={handleChange}
                              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            >
                              <option value={19}>19 %</option>
                              <option value={0}>0 %</option>
                            </select>
                          </div>
                          <div>
                            <label htmlFor="edit-unit" className="block text-sm font-medium text-gray-700">
                              Unité
                            </label>
                            <input
                              type="text"
                              name="unit"
                              id="edit-unit"
                              value={formData.unit}
                              onChange={handleChange}
                              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                              placeholder="Brique, Pot…"
                            />
                          </div>
                        </div>
              <p className="text-xs text-gray-500 -mt-2">
                Le prix unitaire et la TVA servent au calcul des montants de l'avoir.
              </p>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Product Modal */}
      {showDeleteModal && selectedProduct && (
        <Modal
          open
          onClose={() => setShowDeleteModal(false)}
          title="Supprimer le produit"
          size="md"
          footer={
            <>
              <button
                type="button"
                className="modal-btn-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={formLoading}
              >
                Annuler
              </button>
              <button
                type="button"
                className="modal-btn-danger"
                onClick={handleDeleteProduct}
                disabled={formLoading}
              >
                {formLoading ? 'Suppression...' : 'Supprimer'}
              </button>
            </>
          }
        >
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
              <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <p className="text-sm text-gray-500">
                Êtes-vous sûr de vouloir supprimer le produit {selectedProduct.name} ? Cette action est irréversible.
              </p>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ProductManagement;
